import { useEffect, useState } from "react";
import { GraduationCap, Building, Heart, ChevronDown, MessageCircle } from "lucide-react";
import standingImage from "@/assets/about/isabella-standing-sm.jpg";
import seatedImage from "@/assets/about/isabella-seated-sm.jpg";
import presentationImage from "@/assets/about/isabella-presentation.jpeg.asset.json";
import laboratoryImage from "@/assets/about/isabella-laboratory.jpeg.asset.json";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import { useT } from "@/i18n";

const aboutImages = [
  { src: standingImage, de: "Isabella Bartak in Praxiskleidung", en: "Isabella Bartak in practice attire", contain: true },
  { src: seatedImage, de: "Porträt von Isabella Bartak", en: "Portrait of Isabella Bartak", contain: true },
  { src: presentationImage.url, de: "Isabella Bartak bei einem Vortrag über Darmgesundheit", en: "Isabella Bartak presenting on gut health", contain: true },
  { src: laboratoryImage.url, de: "Isabella Bartak mit mikrobiologischen Kulturen", en: "Isabella Bartak with microbiological cultures", contain: true },
];

const qualificationsEn = [
  "Complementary medicine therapist",
  "Expert in bacteriophage therapy",
  "Micronutrient coach",
  "Microbiome therapist",
  "Psychoneuroimmunology",
  "Preamble Psychology",
  "Holistic kinesiology",
  "Myco-molecular therapy",
  "Phytotherapy",
  "Gut health & gut-associated immunology",
  "Hospital Settings Framework and Action Strategies",
  "Hospital Management",
  "ASM Member – American Society of Microbiology",
];

const facharbeitEn =
  "Thesis focus: new microbiological diagnostic options for multi-resistant biofilm infections based on qPCR and NGS reports, combined with alternative treatment concepts from the field of bacteriophage therapy.";

const institutionsEn = [
  { name: "GAMED", description: "Academy for Holistic Medicine" },
  { name: "IU University", description: "International University of Applied Sciences" },
  { name: "OeProm Academy", description: "Microbiome research and probiotic medicine" },
  { name: "Eliava Institute", description: "Institute of Microbiology and Virology" },
  { name: "Biogena Academy", description: "Academy for micronutrients and health" },
  { name: "GFV", description: "Society for Micronutrient Medicine" },
  { name: "IKC", description: "International Kinesiology College" },
];

const qualifications = [
  "Komplementärtherapeutin",
  "Expertin für Bakteriophagentherapie",
  "Mikronährstoffcoach",
  "Mikrobiom-Therapeutin",
  "Psychoneuroimmunologie",
  "Preamble Psychology",
  "Holistische Kinesiologie",
  "Mykomolekulare Therapie",
  "Phytotherapie",
  "Darmgesundheit & darmassoziierte Immunologie",
  "Hospital Settings Framework and Action Strategies",
  "Hospital Management",
  "ASM Member – American Society of Microbiology",
];

const facharbeit =
  "Facharbeitsschwerpunkt: Neue mikrobiologische Diagnostikmöglichkeiten bei multiresistenten Biofilminfektionen auf Basis qPCR und NGS Reports mit alternativen Behandlungskonzepten aus dem Bereich der Bakteriophagentherapie.";

const institutions = [
  { name: "GAMED", description: "Akademie für Ganzheitsmedizin" },
  { name: "IU-Universität", description: "Internationale Hochschule Universität" },
  { name: "OeProm-Akademie", description: "Mikrobiomforschung und probiotische Medizin" },
  { name: "Eliava Institut", description: "Institut für Mikrobiologie und Virologie" },
  { name: "Biogena Academy", description: "Akademie für Mikronährstoffe und Gesundheit" },
  { name: "GFV", description: "Gesellschaft für Vitalstoffmedizin" },
  { name: "IKC", description: "International Kinesiology College" },
];

const interviewDe = [
  {
    question: "Isabella, was hat dich dazu gebracht, deine eigene Praxis zu gründen?",
    answer: [
      "Der Ursprung liegt ganz klar in meiner eigenen Krankheitsgeschichte.",
      "Ich war selbst über viele Jahre Patientin und weiß, wie es sich anfühlt, wenn sich plötzlich ein großer Teil des Lebens nur noch um Gesundheit dreht. Mir wurde über lange Zeit vermittelt, dass meine Erkrankung nicht heilbar sei und ich mich damit arrangieren müsse.",
      "Irgendwann wollte und konnte ich mich damit nicht mehr zufriedengeben.",
      "Ich wollte verstehen. Nicht nur wissen, wie meine Erkrankung heißt oder welches Symptom gerade behandelt wird, sondern: Was passiert eigentlich in meinem Körper? Welche Prozesse hängen miteinander zusammen? Warum reagiert der Körper so? Und gibt es Dinge, die vielleicht noch nicht betrachtet wurden?",
      "Aus dieser persönlichen Suche wurde irgendwann eine fachliche Leidenschaft. Ich begann mich aus- und weiterzubilden, immer tiefer in unterschiedliche Bereiche einzutauchen – und irgendwann entstand daraus der Wunsch nach meiner eigenen Praxis.",
      "Ich wollte einen Ort schaffen, an dem ich so arbeiten kann, wie ich es mir damals selbst gewünscht hätte.",
    ],
  },
  {
    question: "Was hat dich auf diesem Weg am meisten gelehrt – deine Ausbildungen oder deine eigene Erfahrung?",
    answer: [
      "So wichtig meine Ausbildungen für meine heutige Arbeit sind: Am meisten geprägt und gelehrt hat mich wahrscheinlich meine eigene Erfahrung.",
      "Es gibt Dinge, die habe ich in keinem Skriptum, keiner Vorlesung und keiner Ausbildung gelernt.",
      "Ich weiß, wie es sich anfühlt, wenn man von Termin zu Termin geht und trotzdem keine wirkliche Orientierung findet. Ich weiß, was es psychisch mit einem Menschen machen kann, wenn einem über Jahre vermittelt wird: Damit müssen Sie leben.",
      "Und ich weiß, wie leicht man irgendwann beginnt, sich selbst zwischen Beschwerden, Diagnosen, Befunden und Therapieversuchen zu verlieren.",
      "Natürlich ersetzt persönliche Erfahrung keine fundierte fachliche Ausbildung. Aber sie hat mir etwas gegeben, das man nur schwer aus einem Lehrbuch lernen kann: die Perspektive von der anderen Seite.",
      "Meine Ausbildungen haben mir das fachliche Werkzeug gegeben. Meine eigene Geschichte hat mir beigebracht, warum es mir so wichtig ist, dieses Werkzeug einzusetzen.",
    ],
  },
  {
    question: "Dein Ausbildungsweg ist ziemlich vielseitig. Wie hat sich das alles entwickelt?",
    answer: [
      "Mein Ausbildungsweg war definitiv nicht geradlinig.",
      "Ich habe zunächst in unterschiedliche Studienrichtungen hineingeschnuppert, unter anderem in Psychologie und Gesundheitsmanagement. Mich haben schon damals sehr unterschiedliche Fragen interessiert: Was passiert körperlich? Welche Rolle spielt die Psyche? Wie funktioniert unser Gesundheitssystem? Und warum greifen manche Bereiche so wenig ineinander?",
      "Mit der Zeit wurde mein Weg immer spezifischer.",
      "Ich habe mich an verschiedenen Hochschulen, Akademien und Instituten aus- und weitergebildet – unter anderem in Darmgesundheit und probiotischer Medizin, Phytotherapie, Mikronährstoffen, Mykotherapie, Kinesiologie und der 5-Elemente-Lehre sowie Psychoneuroimmunologie. Dazu kamen Bereiche wie Krankenhausmanagement und meine Ausbildung zur diplomierten Verwaltungskraft für medizinische Fachkreise.",
      "Eine besondere Station führte mich nach Tiflis in Georgien, wo ich eine Zertifizierung im Bereich Bakteriophagen absolvierte. Dort wurde mein Interesse an der Mikrobiologie noch einmal deutlich stärker.",
      "Wenn man diese Bereiche einzeln aufzählt, wirkt das vielleicht zunächst ziemlich bunt. Für mich gehören sie aber zusammen.",
      "Ich habe nie Ausbildungen gemacht, um möglichst viele Zertifikate an die Wand hängen zu können. Meistens war es eine Frage, die mich zum nächsten Thema geführt hat.",
      "Und ehrlich gesagt ist das bis heute so: Je mehr ich lerne, desto mehr möchte ich verstehen.",
    ],
  },
  {
    question: "Isabella, du machst ziemlich viel. Aber was machst du eigentlich wirklich?",
    answer: [
      "Das ist tatsächlich eine der schwierigsten Fragen für mich. [lacht]",
      "Bis heute habe ich keinen einzelnen Begriff gefunden, der meine Arbeit wirklich vollständig beschreibt.",
      "Ich beschäftige mich intensiv mit Mikrobiologie und Biochemie, dem Mikrobiom, Laborwerten, Mikronährstoffen, pflanzlichen Wirkstoffen und immunologischen Zusammenhängen. Ich recherchiere, vergleiche, hinterfrage und versuche, Informationen miteinander zu verbinden.",
      "Gleichzeitig bewege ich mich häufig an den Schnittstellen unterschiedlicher Bereiche.",
      "Manchmal bin ich ein bisschen Laborratte, manchmal Detektivin und manchmal Übersetzerin zwischen Labor, Komplementärtherapie und Schulmedizin.",
      "Natürlich lässt sich vieles unter „Komplementärtherapie“ zusammenfassen. Aber irgendwie ist mir dieser Begriff allein zu wenig.",
      "Ich habe mir über die Jahre bewusst Wissen aus unterschiedlichen Bereichen angeeignet, weil ich nicht möchte, dass der Mensch in meine Methode passen muss. Ich möchte herausfinden, welche Perspektive und welcher nächste Schritt zum Menschen vor mir passen könnten.",
      "Vielleicht ist das die einfachste Beschreibung meiner Arbeit: Ich versuche, Zusammenhänge zu verstehen und daraus einen individuellen Weg zu entwickeln.",
    ],
  },
  {
    question: "Bei so viel Medizin, Mikrobiologie und Biochemie – warum bist du eigentlich nicht Ärztin geworden?",
    answer: [
      "Ich habe mich ganz bewusst gegen ein Medizinstudium entschieden.",
      "Ein ganz pragmatischer Grund war die Zeit. Studium und weitere Ausbildung hätten bedeutet, dass viele Jahre vergangen wären, bevor ich überhaupt mit meiner eigentlichen Arbeit hätte beginnen können. Ich wollte früher praktisch arbeiten und meinen eigenen fachlichen Weg weiterentwickeln.",
      "Gleichzeitig habe ich gemerkt, dass ich anders arbeiten möchte.",
      "Das bedeutet überhaupt nicht, dass ich die Schulmedizin ablehne. Ganz im Gegenteil. Sie ist ein wichtiger Bestandteil meiner Arbeit, und ärztliche Diagnostik und Therapie gehören selbstverständlich in ärztliche Hände.",
      "Ich sehe meine Arbeit nicht als Konkurrenz zur Schulmedizin, sondern als Ergänzung. Besonders spannend finde ich genau die Schnittstellen: Wo können Labor, Schulmedizin, Mikrobiologie, Biochemie und komplementäre Ansätze sinnvoll miteinander kommunizieren?",
      "Ich glaube nicht an ein „entweder Schulmedizin oder Komplementärmedizin“. Mich interessiert das Dazwischen und Miteinander.",
    ],
  },
  {
    question: "Prägt dich deine eigene Krankheitsgeschichte heute noch?",
    answer: [
      "Ja. Definitiv – vor allem auch psychisch.",
      "Wenn man über Jahre krank ist, hinterlässt das Spuren. Diese Erfahrungen verschwinden nicht einfach, nur weil man irgendwann an einem anderen Punkt im Leben angekommen ist.",
      "Ich kenne das Gefühl, dem eigenen Körper nicht mehr richtig zu vertrauen. Ich kenne die Unsicherheit, die Angst und dieses ständige Suchen nach Antworten. Und ich weiß, wie sehr Krankheit irgendwann auch die eigene Identität einnehmen kann.",
      "Diese Erfahrungen gehören zu mir und haben mich geprägt.",
      "Gleichzeitig ist mir heute sehr bewusst, dass meine Geschichte eben meine Geschichte ist. Ich darf sie nicht auf einen anderen Menschen übertragen. Jeder erlebt Krankheit anders, jeder bringt andere Voraussetzungen mit und jeder braucht seinen eigenen Weg.",
      "Aber ich glaube, meine Vergangenheit hat mich sensibler dafür gemacht, nicht nur Laborwerte und Symptome zu sehen, sondern auch den Menschen dahinter.",
    ],
  },
  {
    question: "Welches Feedback bekommst du von deinen Klient:innen – und was sagen sie wahrscheinlich am häufigsten zu dir?",
    answer: [
      "Zum Glück bekomme ich sehr viel positives Feedback. Besonders berührt es mich, wenn jemand nach einer langen gesundheitlichen Geschichte erzählt, dass es ihm besser geht oder dass er zum ersten Mal das Gefühl hat, seine Situation und seinen eigenen Körper besser zu verstehen.",
      "Und ich glaube, dass solche Momente auch immer ein kleines Stück in mir selbst mitheilen.",
      "Vielleicht gerade deshalb, weil ich die andere Seite kenne. Wenn ich heute etwas dazu beitragen kann, dass jemand Orientierung findet oder sich etwas zum Positiven verändert, bedeutet mir das sehr viel.",
      "Aber natürlich gibt es auch Grenzen. Es gibt Erkrankungen und Situationen, in denen therapeutische Möglichkeiten begrenzt sind. Es gibt manchmal einen sprichwörtlichen „Point of no Return“. Und manchmal weiß auch ich schlicht nicht weiter.",
      "Diese Ehrlichkeit gehört für mich genauso zu meiner Arbeit. Ich möchte niemandem Heilung versprechen und auch nicht so tun, als gäbe es für alles eine Lösung.",
      "Und was meine Klient:innen am häufigsten zu mir sagen? Das hängt sehr davon ab, wen man fragt. [lacht]",
      "Die einen stellen mir unglaublich viele Fragen – was bei mir natürlich gefährlich ist, weil ich sehr gerne erkläre. Mir ist wichtig, dass mein Gegenüber versteht, was wir eigentlich machen und warum. Was passiert im Körper? Warum schauen wir uns diesen Wert an? Wie könnte das mit einem anderen Prozess zusammenhängen?",
      "Wenn ich dann einmal richtig in einem Thema drinnen bin, kann aus einer kurzen Erklärung durchaus ein kleiner Ausflug durch Mikrobiologie, Biochemie und Stoffwechsel werden. Manche lieben genau das.",
      "Und dann gibt es die andere Fraktion: „Isabella. Bitte. Hör auf zu reden. Sag mir einfach, was ich tun soll.“ [lacht] Das ist auch vollkommen in Ordnung.",
      "Ich musste tatsächlich lernen, dass nicht jeder nach einem langen Tag noch eine spontane Privatvorlesung über Darmbakterien und biochemische Stoffwechselwege hören möchte.",
      "Mittlerweile versuche ich deshalb relativ schnell herauszufinden: Möchtest du verstehen, warum wir etwas machen – oder möchtest du einfach wissen, was du morgen früh tun sollst? Mit beidem kann ich leben.",
    ],
  },
  {
    question: "Was möchtest du Menschen heute in deiner Praxis geben, das du dir damals selbst gewünscht hättest?",
    answer: [
      "Vor allem Orientierung – und das Gefühl, als Mensch wahrgenommen zu werden.",
      "Ich hätte mir damals jemanden gewünscht, der zuhört. Der nicht nur einen einzelnen Laborwert, eine Diagnose oder ein Symptom betrachtet, sondern versucht zu verstehen, wie die verschiedenen Teile zusammenpassen könnten.",
      "Jemanden, bei dem ich mit meinen Fragen nicht das Gefühl gehabt hätte, schwierig zu sein.",
      "Genau das möchte ich heute weitergeben.",
      "Ich möchte nicht, dass jemand meine Praxis verlässt und denkt: Jetzt muss ich noch zehn weitere Dinge ausprobieren.",
      "Ich möchte gemeinsam sortieren: Was wissen wir bereits? Was wissen wir noch nicht? Was könnte relevant sein? Wo sollten wir genauer hinschauen? Wo braucht es ärztliche Abklärung? Und welcher nächste Schritt ist für diesen Menschen überhaupt realistisch?",
      "Ich werde nicht für jeden Menschen eine Antwort haben. Und ich werde auch nicht jedem helfen können.",
      "Aber ich kann versprechen, genau hinzusehen, Fragen zu stellen und meine eigenen Grenzen genauso ernst zu nehmen wie meine Möglichkeiten.",
      "Vielleicht ist genau das der Kern meiner Arbeit.",
      "Meine eigene Geschichte hat mit einer langen Suche nach Antworten begonnen. Heute möchte ich anderen Menschen dabei helfen, auf ihrem eigenen Weg ein Stück mehr Orientierung zu finden.",
    ],
  },
];

const interviewEn = interviewDe.map(({ question, answer }) => ({ question, answer }));

const About = () => {
  const t = useT(
    {
      en: false,
      imageAlt: "Isabella – Dipl. & Zert. Komplementärtherapeutin",
      name: "Isabella Bartak",
      role: "akad. dipl. zert. komplementärmedizinische Therapeutin",
      journeyTitle: "Von der Patientin zur Therapeutin",
      journeyText:
        "Als jemand, der selbst die Herausforderungen dunkler Zeiten durchlebt hat, verstehe ich die damit verbundenen Schwierigkeiten aus erster Hand. Diese Erfahrungen haben mich nicht nur geprägt, sondern auch gezeigt, wie entscheidend es ist, die eigene Gesundheit wieder in Balance zu bringen und langfristig zu erhalten. Mein Ziel ist es, Sie auf Ihrem Weg zu unterstützen und gemeinsam Strategien zu entwickeln, um Ihr Wohlbefinden nachhaltig zu stärken.",
      more: "Mehr über meinen Weg lesen →",
      dialogTitle: "Warum ich das tue, was ich heute tue?",
      dialog: [
        "Jeder von uns hat seine eigene Geschichte, und meine begann in einem dunklen Kapitel meines Lebens, als ich selbst mit einer schweren Krankheit kämpfte. Die Odysee durch das Gesundheitssystem, die endlosen Arztbesuche und die verzweifelte Suche nach Antworten waren geprägt von Schmerz und Unsicherheit. In diesen Momenten fühlte ich mich oft allein und verloren – besonders als mir gesagt wurde, dass ich austherapiert sei.",
        "Doch ich habe nie aufgegeben. Ich habe Wege gefunden, meine Gesundheit zurückzugewinnen, und heute bin ich wieder gesund. Diese Reise hat in mir den tiefen Wunsch geweckt, anderen zu helfen, die in ähnlichen Situationen stecken. Ich weiß, wie es sich anfühlt, in der Dunkelheit zu stehen und nach einem Lichtblick zu suchen.",
        "Es ist oftmals zugegeben die eigene Erinnerung an meine Geschichte, die mich motiviert, mich für Menschen einzusetzen, die denselben Herausforderungen gegenüberstehen. Ich möchte Ihnen nicht nur die Ressourcen geben, die Sie benötigen, sondern auch das Gefühl, dass Sie nicht allein sind. Gemeinsam können wir Wege finden, um Ihre Lebensqualität zu verbessern und Ihre Stärke zu entfalten.",
        "Mein Ziel ist es, Ihnen Unterstützung und Hoffnung zu bieten. Ich verstehe die Herausforderungen, die mit einer Krankheit einhergehen, und ich weiß, wie wichtig es ist, einen vertrauensvollen Ansprechpartner zu haben. Lassen Sie uns gemeinsam daran arbeiten, eine positive Veränderung in Ihrem Leben herbeizuführen. Ihre Gesundheit und Ihr Wohlbefinden ist nicht nur \u201emeine berufliche Mission\u201c – sondern mein Herzensanliegen.",
      ],
      eyebrow: "Die Person hinter der Praxis",
      heading: "Über mich",
      p1: "Mein beruflicher Werdegang im Gesundheitssektor ist stark von meiner persönlichen gesundheitlichen Vorgeschichte geprägt. Deswegen liegt es mir so wahnsinnig am Herzen anderen Menschen zu helfen wieder mehr Wohlbefinden in ihrem Leben zu erlangen.",
      p2: "In meiner Praxis als dipl. & zert. Komplementärtherapeutin kombiniere ich verschiedene wissenschaftlich fundierte Ansätze und führe sie zusammen, weil ich der Meinung bin, dass Gesundheit ebenfalls viele verschiedene Säulen beinhaltet.",
      p3: "Mein Ziel ist es, durch gezielte Interventionen ganzheitliche Strategien zu fördern.",
      qualiLabel: "Qualifikationen und Spezialisierungen",
      instiLabel: "Ausbildungsinstitute",
      interviewLabel: "Im Gespräch mit Isabella (Interview)",
    },
    {
      en: true,
      imageAlt: "Isabella – certified complementary medicine therapist",
      name: "Isabella Bartak",
      role: "certified complementary medicine therapist (academic degree)",
      journeyTitle: "From patient to therapist",
      journeyText:
        "Having lived through the challenges of dark times myself, I understand the difficulties they bring first-hand. Those experiences shaped me and showed me how crucial it is to bring your health back into balance and to keep it there. My aim is to support you on your path and to develop strategies together that strengthen your well-being in the long term.",
      more: "Read more about my journey →",
      dialogTitle: "Why I do what I do today",
      dialog: [
        "Each of us has our own story, and mine began in a dark chapter of my life when I was fighting a serious illness myself. The odyssey through the healthcare system, the endless medical appointments and the desperate search for answers were marked by pain and uncertainty. In those moments I often felt alone and lost – especially when I was told there was nothing more to be done for me.",
        "But I never gave up. I found ways to regain my health, and today I am well again. That journey awakened a deep wish in me to help others in similar situations. I know what it feels like to stand in the dark and search for a glimmer of light.",
        "It is, I admit, often the memory of my own story that motivates me to stand up for people facing the same challenges. I want to give you not only the resources you need, but also the feeling that you are not alone. Together we can find ways to improve your quality of life and unfold your strength.",
        "My aim is to offer you support and hope. I understand the challenges that come with illness, and I know how important it is to have someone you can trust. Let us work together towards a positive change in your life. Your health and well-being are not merely my professional mission – they are close to my heart.",
      ],
      eyebrow: "The person behind the practice",
      heading: "About me",
      p1: "My professional path in healthcare is strongly shaped by my own health history. That is why helping other people regain well-being in their lives means so much to me.",
      p2: "In my practice as a certified complementary medicine therapist I combine various scientifically grounded approaches and bring them together, because I believe that health, too, rests on many different pillars.",
      p3: "My goal is to foster holistic strategies through targeted interventions.",
      qualiLabel: "Qualifications and specialisations",
      instiLabel: "Training institutes",
      interviewLabel: "In conversation with Isabella (interview in German)",
    },
  );
  const quals = t.en ? qualificationsEn : qualifications;
  const insts = t.en ? institutionsEn : institutions;
  const interview = t.en ? interviewEn : interviewDe;
  // Interview-Bereich vorübergehend offline (auf false gesetzt; Inhalt bleibt erhalten)
  const SHOW_INTERVIEW = false;
  const [qualiOpen, setQualiOpen] = useState(false);
  const [instiOpen, setInstiOpen] = useState(false);
  const [interviewOpen, setInterviewOpen] = useState(false);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    if (!carouselApi) return;

    const updateSelectedImage = () => setSelectedImage(carouselApi.selectedScrollSnap());
    updateSelectedImage();
    carouselApi.on("select", updateSelectedImage);
    carouselApi.on("reInit", updateSelectedImage);

    return () => {
      carouselApi.off("select", updateSelectedImage);
      carouselApi.off("reInit", updateSelectedImage);
    };
  }, [carouselApi]);

  return <section id="ueber-mich" className="py-16 md:py-24 bg-secondary/50">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Profile Image + Personal Journey Box */}
            <div className="space-y-6">
              <div className="relative mx-auto w-full max-w-[19rem] md:max-w-[22rem] lg:max-w-[24rem]">
                <Carousel setApi={setCarouselApi} opts={{ loop: true }} className="group">
                  <CarouselContent className="ml-0">
                    {aboutImages.map((image) => (
                      <CarouselItem key={image.src} className="pl-0">
                        <div className="aspect-[4/5] overflow-hidden rounded-2xl bg-card shadow-lg transition-shadow duration-500 group-hover:shadow-2xl">
                          <img
                            src={image.src}
                            alt={t.en ? image.en : image.de}
                            loading="lazy"
                            decoding="async"
                            className={`h-full w-full ${image.contain ? "object-contain object-bottom" : "object-cover object-center"}`}
                          />
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious
                    className="left-3 border-primary/20 bg-background/90 text-primary shadow-md hover:bg-background"
                    aria-label={t.en ? "Previous image" : "Vorheriges Bild"}
                  />
                  <CarouselNext
                    className="right-3 border-primary/20 bg-background/90 text-primary shadow-md hover:bg-background"
                    aria-label={t.en ? "Next image" : "Nächstes Bild"}
                  />
                </Carousel>
                <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-2" aria-label={t.en ? "Choose image" : "Bild auswählen"}>
                  {aboutImages.map((image, index) => (
                    <Button
                      key={image.src}
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 rounded-full bg-background/85 p-0 shadow-sm hover:bg-background"
                      onClick={() => carouselApi?.scrollTo(index)}
                      aria-label={`${t.en ? "Show image" : "Bild anzeigen"} ${index + 1}`}
                      aria-current={selectedImage === index ? "true" : undefined}
                    >
                      <span className={`h-2.5 w-2.5 rounded-full transition-colors ${selectedImage === index ? "bg-primary" : "bg-muted-foreground/40"}`} />
                    </Button>
                  ))}
                </div>
                {/* Decorative Element */}
                <div className="absolute -bottom-6 -right-2 w-48 h-48 bg-accent/20 rounded-full blur-3xl -z-10" />
              </div>

              {/* Name Badge */}
              <div className="relative -mt-10 z-10 mx-auto w-full max-w-[19rem] md:max-w-[22rem] lg:max-w-[24rem]">
                <div className="bg-white/95 backdrop-blur-sm rounded-xl px-5 py-3.5 shadow-lg border border-primary/15 text-center">
                  <h3 className="text-base font-bold text-foreground tracking-tight">{t.name}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">{t.role}</p>
                </div>
              </div>

              {/* Personal Journey Box */}
              <div className="relative p-6 rounded-xl bg-gradient-to-br from-orange-50 via-amber-50 to-primary/10 border border-orange-200/50 shadow-sm">
                <div className="absolute -top-3 -left-3 w-10 h-10 bg-gradient-to-br from-primary to-orange-400 rounded-full flex items-center justify-center shadow-md">
                  <Heart className="w-5 h-5 text-white fill-white" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2 ml-4">{t.journeyTitle}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t.journeyText}</p>
                
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="link" className="text-primary hover:text-primary/80 p-0 h-auto mt-3 text-sm font-medium">
                      {t.more}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-gradient-to-br from-orange-50 via-amber-50 to-primary/10 border-orange-200/50">
                    <DialogHeader>
                      <DialogTitle className="text-xl font-bold text-foreground flex items-center gap-2">
                        <Heart className="w-5 h-5 text-orange-500 fill-orange-500" />
                        {t.dialogTitle}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 text-foreground/80 leading-relaxed text-sm">
                      {t.dialog.map((para) => (
                        <p key={para.slice(0, 24)}>{para}</p>
                      ))}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Content */}
            <div>
              <span className="text-primary font-medium text-sm uppercase tracking-wider">
                {t.eyebrow}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2 mb-6">{t.heading}</h2>

              <p className="text-lg text-muted-foreground leading-relaxed mb-4">{t.p1}</p>

              <p className="text-lg text-muted-foreground leading-relaxed mb-6">{t.p2}</p>

              <p className="text-primary font-semibold mb-6">{t.p3}</p>




              {/* Qualifications - ausklappbar */}
              <div className="mb-4">
                <Collapsible open={qualiOpen} onOpenChange={setQualiOpen}>
                  <CollapsibleTrigger className="flex items-center justify-between w-full gap-2 p-4 bg-card border border-border rounded-xl hover:shadow-sm transition-shadow">
                    <div className="flex items-center gap-2">
                      <GraduationCap className="w-5 h-5 text-primary" />
                      <span className="font-medium text-foreground">{t.qualiLabel}</span>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-primary transition-transform duration-300 ${qualiOpen ? "rotate-180" : ""}`} />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                    <div className="flex flex-wrap gap-2 pt-4 px-1">
                      {quals.map(qual => (
                        <span key={qual} className="px-3 py-1.5 bg-primary/10 text-primary text-sm rounded-full">
                          {qual}
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground italic mt-3 px-1">{t.en ? facharbeitEn : facharbeit}</p>
                  </CollapsibleContent>
                </Collapsible>
              </div>

              {/* Institutions - ausklappbar */}
              <div className="mb-6">
                <Collapsible open={instiOpen} onOpenChange={setInstiOpen}>
                  <CollapsibleTrigger className="flex items-center justify-between w-full gap-2 p-4 bg-card border border-border rounded-xl hover:shadow-sm transition-shadow">
                    <div className="flex items-center gap-2">
                      <Building className="w-5 h-5 text-primary" />
                      <span className="font-medium text-foreground">{t.instiLabel}</span>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-primary transition-transform duration-300 ${instiOpen ? "rotate-180" : ""}`} />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                    <div className="flex flex-wrap gap-3 pt-4 px-1">
                      {insts.map(inst => (
                        <div key={inst.name} className="px-4 py-2 bg-card border border-border rounded-lg">
                          <span className="font-semibold text-foreground">{inst.name}</span>
                          <span className="block text-xs text-muted-foreground">{inst.description}</span>
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>

              {/* Interview - ausklappbar, Fragen einzeln ausklappbar (derzeit offline, Inhalt bleibt erhalten) */}
              {SHOW_INTERVIEW && (
              <div className="mb-6">
                <Collapsible open={interviewOpen} onOpenChange={setInterviewOpen}>
                  <CollapsibleTrigger className="flex items-center justify-between w-full gap-2 p-4 bg-card border border-border rounded-xl hover:shadow-sm transition-shadow">
                    <div className="flex items-center gap-2 text-left">
                      <MessageCircle className="w-5 h-5 shrink-0 text-primary" />
                      <span className="font-medium text-foreground">{t.interviewLabel}</span>
                    </div>
                    <ChevronDown className={`w-5 h-5 shrink-0 text-primary transition-transform duration-300 ${interviewOpen ? "rotate-180" : ""}`} />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                    <Accordion type="single" collapsible className="mt-3 rounded-xl border border-border bg-card px-4">
                      {interview.map((item, index) => (
                        <AccordionItem key={item.question} value={`interview-${index}`}>
                          <AccordionTrigger className="text-left text-sm font-semibold leading-snug hover:no-underline md:text-base">
                            <span>{index + 1}. {item.question}</span>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="space-y-3 text-sm leading-relaxed text-muted-foreground md:text-base">
                              {item.answer.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CollapsibleContent>
                </Collapsible>
              </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default About;